/* eslint-disable camelcase */
import React, { ReactElement, FC, ReactNode } from 'react';

import { getHttps } from '../../../core/util';
import { DEEPSEA_MODELS } from '../../../settings';
import Button from '../../ui/Button';
import JobStatusAlert from '../../shared/JobStatusAlert';
import formatJobDate from '../../shared/jobDateFormatter';
import JobDetailsTable from '../../shared/JobDetailsTable';
import { EmptyObject } from '../../shared/sharedTypes';

// Styles now inline

interface DeepSeaModel {
  name: string;
  vizInputTypes: string[];
}

interface JobFile {
  url: string;
}

interface JobFiles {
  gz_file?: JobFile;
}

export interface Job {
  job_id: string;
  ds_model: string;
  input_type: string;
  insilico?: boolean;
  genome_assembly?: string;
  email?: string;
  created?: string;
  status?: string;
  title: string;
  non_field_errors?: string[];
  files?: JobFiles;
}

interface JobStatusTableProps {
  job: Job | EmptyObject;
  statusOnly?: boolean;
  isChecking?: boolean;
  hasPollFailed?: boolean;
}

function hasVizResult(job: Job | EmptyObject): boolean {
  const dsModel = DEEPSEA_MODELS.find(
    (model: DeepSeaModel) =>
      model.name.toLowerCase() === job.ds_model?.toLowerCase(),
  );
  return (
    (job.input_type === 'fasta' && job.insilico === true) ||
    (dsModel !== undefined && dsModel.vizInputTypes.includes(job.input_type))
  );
}

const JobStatusTable: FC<JobStatusTableProps> = ({
  job,
  statusOnly = false,
  isChecking = false,
  hasPollFailed = false,
}) => {
  let statusTable: ReactNode = '';

  if (job) {
    let createdRow: ReactElement | null = null;
    if (job.created) {
      createdRow = (
        <tr>
          <th scope="row">Created</th>
          <td>{formatJobDate(job.created)}</td>
        </tr>
      );
    }

    let genomeRow: ReactElement | null = null;
    if (job.genome_assembly && job.input_type !== 'fasta') {
      genomeRow = (
        <tr>
          <th scope="row">Genome</th>
          <td>{job.genome_assembly}</td>
        </tr>
      );
    }

    let emailRow: ReactElement | null = null;
    if (job.email) {
      emailRow = (
        <tr>
          <th scope="row">Email</th>
          <td>{job.email}</td>
        </tr>
      );
    }

    let jobIDCell: ReactElement;
    if (hasVizResult(job)) {
      jobIDCell = (
        <td>
          <a href={`/deepsea/jobs/${job.job_id}/`}>{job.job_id}</a>
        </td>
      );
    } else {
      jobIDCell = <td>{job.job_id}</td>;
    }

    if (job.job_id || emailRow || createdRow) {
      statusTable = (
        <JobDetailsTable caption="Job details">
          {/* Boolean guards keep empty strings out of the table's children. */}
          {!!job.ds_model && (
            <tr>
              <th scope="row">DeepSEA model</th>
              <td>{job.ds_model}</td>
            </tr>
          )}
          {!!(job.insilico || job.input_type) && (
            <tr>
              <th scope="row">Submission type</th>
              <td>{job.insilico ? 'in silico mutagenesis' : job.input_type}</td>
            </tr>
          )}
          {genomeRow}
          {!!job.job_id && (
            <tr>
              <th scope="row">Job ID</th>
              {jobIDCell}
            </tr>
          )}
          {createdRow}
          {emailRow}
        </JobDetailsTable>
      );
    }
  }

  let jobStatus = job.status;
  if (!jobStatus && job.non_field_errors && job.non_field_errors.length > 0) {
    jobStatus = `error: ${job.non_field_errors[0]}`;
  }

  const downloadButton =
    job.files && job.files.gz_file ? (
      <Button href={getHttps(job.files.gz_file.url) as string}>
        Download Results
      </Button>
    ) : null;

  const viewButton =
    hasVizResult(job) && !job.status?.startsWith('error') ? (
      <Button bsStyle="primary" href={`/deepsea/jobs/${job.job_id}/`}>
        View Results
      </Button>
    ) : null;

  return (
    <div style={{ width: '100%' }}>
      {!!job.title && (
        <div
          style={{ marginLeft: '5px', marginTop: '25px', marginBottom: '10px' }}
        >
          <h4>{job.title}</h4>
        </div>
      )}
      <JobStatusAlert
        status={isChecking ? undefined : jobStatus}
        runningSuffix=" (processing time varies based on the size of your query)"
        hasPollFailed={hasPollFailed}
      />

      {statusTable}
      {statusOnly ? null : (
        <div style={{ textAlign: 'center' }}>
          {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
          {viewButton} {downloadButton}
        </div>
      )}
    </div>
  );
};

export default JobStatusTable;
